import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { CreateDealershipHttp, DealershipDto, FindManyDealershipsHttp } from '@mmscan/mmscan-api-types';
import type { CursorListReply, Reply, ResourceCreated } from '@mmscan/shared-types';
import type { Observable } from 'rxjs';

import { MMSCAN_API_BASE_URL } from '../mmscan-api.config';

export type {
  CreateDealershipHttp,
  DealershipDto,
  FindManyDealershipsHttp,
  FindManyDealershipsHttpInitial,
  UpdateDealershipHttp,
} from '@mmscan/mmscan-api-types';

/**
 * Service for managing dealerships via mmscan-api REST endpoints
 *
 * @example
 * ```typescript
 * private readonly dealershipService = inject(DealershipService);
 *
 * getDealerships() {
 *   this.dealershipService.findMany({ limit: 20, sortBy: 'createdAt', sortOrder: 'DESC' })
 *     .subscribe(result => console.log(result));
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DealershipService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(MMSCAN_API_BASE_URL);

  private get url(): string {
    return `${this.baseUrl}/v1/dealerships`;
  }

  findMany(params: FindManyDealershipsHttp): Observable<CursorListReply<DealershipDto>> {
    return this.http.get<CursorListReply<DealershipDto>>(this.url, { params });
  }

  findOne(id: string): Observable<Reply<DealershipDto>> {
    return this.http.get<Reply<DealershipDto>>(`${this.url}/${id}`);
  }

  create(body: CreateDealershipHttp): Observable<ResourceCreated> {
    return this.http.post<ResourceCreated>(this.url, body);
  }
}
